import { gql } from '@apollo/client';
import {
  chakra,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { useAuth } from '~auth/useAuth';
import {
  BriefcaseIcon,
  DisplaysIcon,
  ExternalLinkIcon,
  LogoutIcon,
  MoreIconVertical,
  PendingIcon,
  TrashIcon,
  WarningIcon,
} from '~components/ui/icons';
import {
  ServiceAdminOrgAccessRequestFilter,
  ServiceAdminOrgAccessRequestStatus,
} from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { goToOrganization } from '~utils/elevatedOrganization';
import {
  SentAccessRequestsQuery,
  useSentAccessRequestsQuery,
} from './__generated__/SentAccessRequests.graphql';
import { AccessRequestColumn, AccessRequestsSection, TruncatedCell } from './AccessRequestsSection';
import { formatExpiryDate, getEffectiveStatus } from './accessRequestUtils';
import { ActivityLogsModal } from './ActivityLogsModal';
import { DeviceLogsModal } from './DeviceLogsModal';

type SentAccessRequest = SentAccessRequestsQuery['myAccessRequests'][number];

export function SentAccessRequests() {
  const [filter, setFilter] = useState(ServiceAdminOrgAccessRequestFilter.All);
  const { data, loading, error } = useSentAccessRequestsQuery({
    variables: { filter },
    fetchPolicy: 'cache-and-network',
  });

  const columns = useMemo<Array<AccessRequestColumn<SentAccessRequest>>>(
    () => [
      {
        id: 'name',
        header: t('orgName'),
        render: (request) => (
          <TruncatedCell
            text={request.targetOrganizationName ?? request.targetOrganizationId}
            maxWidth="48"
            fontWeight="semibold"
          />
        ),
      },
      {
        id: 'ownerEmail',
        header: t('ownerEmail'),
        render: (request) => (
          <TruncatedCell text={request.targetOrganizationOwnerEmail ?? '-'} maxWidth="64" />
        ),
      },
      {
        id: 'displayCount',
        header: t('displayCount'),
        render: (request) => request.targetOrganizationDisplayCount ?? '-',
      },
      { id: 'expiry', header: t('requestAccessExpiry'), render: formatExpiryDate },
      { id: 'status', header: '', render: (request) => <SentRequestStatus request={request} /> },
      {
        id: 'actions',
        header: '',
        width: '16',
        render: (request) => <SentRequestActions request={request} />,
      },
    ],
    [],
  );

  if (error) {
    return <HandleApiError error={error} />;
  }

  return (
    <AccessRequestsSection
      icon={LogoutIcon}
      title={t('sentRequests')}
      description={t('sentRequestsDescription')}
      filter={filter}
      onFilterChange={setFilter}
      isLoading={loading && !data}
      rows={data?.myAccessRequests ?? []}
      columns={columns}
    />
  );
}

function SentRequestStatus({ request }: { request: SentAccessRequest }) {
  switch (getEffectiveStatus(request)) {
    case ServiceAdminOrgAccessRequestStatus.Approved:
      return (
        <HStack
          as="button"
          color="green.500"
          onClick={() => goToOrganization(request.targetOrganizationId)}
        >
          <ExternalLinkIcon />
          <chakra.span fontWeight="medium">{t('accessAvailableGoToOrg')}</chakra.span>
        </HStack>
      );
    case ServiceAdminOrgAccessRequestStatus.Awaiting:
      return (
        <HStack color="blue.300">
          <PendingIcon />
          <chakra.span fontWeight="medium">{t('awaitingAccessApproval')}</chakra.span>
        </HStack>
      );
    case ServiceAdminOrgAccessRequestStatus.Rejected:
      return (
        <HStack color="orange.400">
          <WarningIcon />
          <chakra.span fontWeight="medium">{t('accessRequestRejected')}</chakra.span>
        </HStack>
      );
    case ServiceAdminOrgAccessRequestStatus.Revoked:
      return (
        <HStack color="red.500">
          <WarningIcon />
          <chakra.span fontWeight="medium">{t('accessRevoked')}</chakra.span>
        </HStack>
      );
    default:
      return (
        <HStack color="gray.400">
          <WarningIcon />
          <chakra.span fontWeight="medium">{t('accessRequestExpired')}</chakra.span>
        </HStack>
      );
  }
}

function SentRequestActions({ request }: { request: SentAccessRequest }) {
  const activityLogs = useDisclosure();
  const deviceLogs = useDisclosure();
  const { verifyUserPermissions } = useAuth();

  return (
    <>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          variant="unstyled"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minWidth="8"
          height="8"
          aria-label={t('actions')}
          icon={<MoreIconVertical color="blue.400" width="5" height="5" />}
        />
        <MenuList>
          <MenuItem icon={<BriefcaseIcon />} onClick={activityLogs.onOpen}>
            {t('activityLogs')}
          </MenuItem>
          <MenuItem
            icon={<DisplaysIcon />}
            isDisabled={!verifyUserPermissions([Permission.ServiceAdminDeviceLogsView])}
            onClick={deviceLogs.onOpen}
          >
            {t('deviceLogs')}
          </MenuItem>
          <MenuItem icon={<TrashIcon color="red.500" />} color="red.500" isDisabled>
            {t('delete')}
          </MenuItem>
        </MenuList>
      </Menu>
      <ActivityLogsModal
        accessRequestId={request.id}
        organizationName={request.targetOrganizationName ?? request.targetOrganizationId}
        isOpen={activityLogs.isOpen}
        onClose={activityLogs.onClose}
      />
      <DeviceLogsModal
        accessRequestId={request.id}
        isOpen={deviceLogs.isOpen}
        onClose={deviceLogs.onClose}
      />
    </>
  );
}

SentAccessRequests.graphql = {
  queries: {
    SentAccessRequests: gql`
      query SentAccessRequests($filter: ServiceAdminOrgAccessRequestFilter) {
        myAccessRequests(filter: $filter) {
          id
          targetOrganizationId
          targetOrganizationName
          targetOrganizationDisplayCount
          targetOrganizationOwnerEmail
          status
          createdAt
          accessExpiresAt
        }
      }
    `,
  },
};
